import { createAsyncThunk } from '@reduxjs/toolkit'

import UploadProgress from '../../components/widgets/UploadProgress'
import userManager from '../../services/UserManager'
import {
  DEFAULT_AUDIO_FILE_TYPE,
  DEFAULT_IMAGE_FILE_TYPE,
  DEFAULT_PLAYLIST_KEY,
  defaultTrackInfo,
} from '../../constants/constants'
import { AppDispatch, RootState } from '../../reducers'
import blasterPeersSlice from '../../reducers/blasterPeersSlice'
import { selectOwnedPlaylistMap, selectTrackInfo } from '../../selectors/blaster-peer-selectors'
import {
  selectCurrentLyrics,
  selectCurrentTrackInfo,
  selectCurrentTrackMeta,
} from '../../selectors/current-play-selectors'
import {
  selectIsInLocalTracks,
  selectLocalTrackInfoForState,
} from '../../selectors/local-authoring-selectors'
import { selectCurrentUsername } from '../../selectors/session-selectors'
import { TrackInfo, UploadHelpers } from '../../types'
import { updateTrack } from '../social/peer-playlists'
import {
  updateLocalTiming,
  updateDirty,
  updateLocalTimingURL,
  updateLocalAudioURL,
  updateLocalOwner,
  updateLocalImageURL,
  addTrackToLocalPlaylist,
} from './update-local-track'

const uploadTrackTiming = createAsyncThunk<
  Promise<any>,
  string,
  { state: RootState; dispatch: AppDispatch }
>('upload/timing', async (timing: string, { dispatch, getState }) => {
  const state = getState()
  const meta = selectCurrentTrackMeta(state)
  const trackSlug = state.currentPlay.trackInfo.slug
  const timingWithMeta = meta.concat(timing)
  dispatch(updateLocalTiming(trackSlug, timing))

  const uploadProgress = new UploadProgress('timing-uploadProgress')
  uploadProgress.start()
  const params = userManager.getS3BucketParams(trackSlug, 'text/lrc', timingWithMeta)

  return userManager
    .doS3Upload(params, (percentUploaded: number) => {
      uploadProgress.doProgress(percentUploaded)
    })
    .then(
      (remoteUrl: string) => {
        uploadProgress.stop()
        dispatch(updateLocalTimingURL(trackSlug, remoteUrl))
        dispatch(updateDirty(trackSlug, { isTimingDirty: false }))
      },
      (err) => {
        uploadProgress.stop(err)
      }
    )
})

const uploadTrackManifest = createAsyncThunk<
  Promise<any>,
  { localAudioFileType: string; localImageFileType: string; cloneFrom: string },
  { state: RootState; dispatch: AppDispatch }
>(
  'upload/manifest',
  async ({ localAudioFileType, localImageFileType, cloneFrom }, { dispatch, getState }) => {
    const state = getState()
    const trackInfo = selectCurrentTrackInfo(state)
    const { slug: trackSlug } = trackInfo
    const manifest = JSON.stringify(
      userManager.getPackageData({ trackSlug, localAudioFileType, localImageFileType, cloneFrom })
    )
    if (!manifest) {
      return Promise.resolve()
    }
    const uploadProgress = new UploadProgress('manifest-uploadProgress')
    uploadProgress.start()
    const params = userManager.getS3BucketParams(trackSlug, 'text/json', manifest)

    return userManager
      .doS3Upload(params, (percentUploaded: number) => {
        uploadProgress.doProgress(percentUploaded)
      })
      .then(
        (remoteUrl) => {
          uploadProgress.stop()
          dispatch(updateDirty(trackSlug, { isInfoDirty: false }))
          dispatch(updateTrack({ trackInfo })) // TODO: needed??
        },
        function (err) {
          uploadProgress.stop(err)
          Promise.reject(err)
        }
      )
  }
)

const uploadTrackLyrics = createAsyncThunk<
  Promise<any>,
  void,
  { state: RootState; dispatch: AppDispatch }
>('upload/lyrics', async (_, { dispatch, getState }) => {
  const state = getState()
  const meta = selectCurrentTrackMeta(state)
  const lyrics = selectCurrentLyrics(state)
  const lyricsWithMeta = meta.concat(lyrics)
  const trackInfo = selectCurrentTrackInfo(state)
  const { slug: trackSlug } = trackInfo
  const uploadProgress = new UploadProgress('lyrics-uploadProgress')
  uploadProgress.start()
  const params = userManager.getS3BucketParams(trackSlug, 'text/plain', lyricsWithMeta)

  return userManager
    .doS3Upload(params, (percentUploaded: number) => {
      uploadProgress.doProgress(percentUploaded)
    })
    .then(
      (remoteUrl) => {
        uploadProgress.stop()
        dispatch(updateDirty(trackSlug, { isLyricsDirty: false }))
        dispatch(updateTrack({ trackInfo }))
      },
      function (err) {
        uploadProgress.stop(err)
        Promise.reject(err)
      }
    )
})

const uploadTrackAudio = createAsyncThunk<
  Promise<any>,
  File,
  { state: RootState; dispatch: AppDispatch }
>('upload/audio', async (trackFile: File, { dispatch, getState }) => {
  const state = getState()
  const { trackInfo } = state.currentPlay
  const { slug: trackSlug } = trackInfo
  if (!trackFile) {
    return Promise.reject() // none selected
  }
  const audioFileType = trackFile.type || DEFAULT_AUDIO_FILE_TYPE
  const uploadProgress = new UploadProgress('audio-uploadProgress')
  uploadProgress.start()
  const params = userManager.getS3BucketParams(trackSlug, audioFileType, trackFile)

  return userManager
    .doS3Upload(params, (percentUploaded: number) => {
      uploadProgress.doProgress(percentUploaded)
    })
    .then(
      (remoteUrl) => {
        uploadProgress.stop()
        dispatch(updateLocalAudioURL(trackSlug, remoteUrl))
        dispatch(updateDirty(trackSlug, { isAudioDirty: false }))
      },
      (err) => {
        uploadProgress.stop(err)
      }
    )
})

const uploadTrackImage = createAsyncThunk<
  Promise<any>,
  File,
  { state: RootState; dispatch: AppDispatch }
>('upload/image', async (trackFile: File, { dispatch, getState }) => {
  const state = getState()
  const { trackInfo } = state.currentPlay
  const { slug: trackSlug } = trackInfo
  if (!trackFile) {
    return Promise.reject('no track image')
  }
  const imageFileType = trackFile.type || DEFAULT_IMAGE_FILE_TYPE
  const uploadProgress = new UploadProgress('image-uploadProgress')
  uploadProgress.start()
  const params = userManager.getS3BucketParams(trackSlug, imageFileType, trackFile)

  return userManager
    .doS3Upload(params, (percentUploaded: number) => {
      uploadProgress.doProgress(percentUploaded)
    })
    .then(
      (remoteUrl) => {
        uploadProgress.stop()
        dispatch(updateLocalImageURL(trackSlug, remoteUrl))
        dispatch(updateDirty(trackSlug, { isImageDirty: false }))
      },
      (err) => {
        uploadProgress.stop(err)
      }
    )
})

const uploadAllDirty = createAsyncThunk<
  void,
  { uploadHelpers: UploadHelpers; effectiveDirtyState: any },
  { state: RootState; dispatch: AppDispatch }
>('upload/all-dirty', async ({ uploadHelpers, effectiveDirtyState }, { dispatch, getState }) => {
  const { getLocalAudioFile, getLocalImageFile, getCurrentLRC } = uploadHelpers
  const state = getState()
  const currentUsername = selectCurrentUsername(state)
  const currentTrackInfo = selectCurrentTrackInfo(state)
  const { slug: trackSlug, owner: currentOwner } = currentTrackInfo
  const ownedPlaylistMap = selectOwnedPlaylistMap(currentUsername)(state)
  const { trackOrder: userAllTracks = [] } = ownedPlaylistMap[DEFAULT_PLAYLIST_KEY]
  const isNew = !userAllTracks.includes(trackSlug)

  const cloneFrom = currentUsername === currentOwner ? '' : currentOwner
  if (cloneFrom) {
    dispatch(updateLocalOwner(trackSlug, currentUsername))
  }
  const dirtyState = effectiveDirtyState || state.currentPlay.dirtyState
  const uploadPromises: Promise<any>[] = []
  if (!cloneFrom && dirtyState.isAudioDirty) {
    const audioFile = getLocalAudioFile()
    if (audioFile) {
      uploadPromises.push(dispatch(uploadTrackAudio(audioFile)))
    } else {
      console.warn('isAudioDirty but no local audio file?')
    }
  }
  if (!cloneFrom && dirtyState.isImageDirty) {
    const imageFile = getLocalImageFile()
    if (imageFile) {
      uploadPromises.push(dispatch(uploadTrackImage(imageFile)))
    } else {
      console.warn('isImageDirty but no local image file?')
    }
  }
  if (cloneFrom || dirtyState.isInfoDirty) {
    const audioFile = getLocalAudioFile()
    const localAudioFileType = audioFile ? audioFile.type : ''
    const imageFile = getLocalImageFile()
    const localImageFileType = imageFile ? imageFile.type : ''
    uploadPromises.push(
      dispatch(uploadTrackManifest({ localAudioFileType, localImageFileType, cloneFrom }))
    )
  }
  if (cloneFrom || dirtyState.isLyricsDirty) {
    uploadPromises.push(dispatch(uploadTrackLyrics()))
  }
  if (cloneFrom || dirtyState.isTimingDirty) {
    const timing = getCurrentLRC()
    uploadPromises.push(dispatch(uploadTrackTiming(timing)))
  }
  await Promise.all(uploadPromises)
  if (isNew) {
    const trackInfo: TrackInfo = selectLocalTrackInfoForState(trackSlug)(state)
    dispatch(addTrackToLocalPlaylist({ tracksToAdd: [trackInfo] }))
  }
})

const adoptCurrentTrack = createAsyncThunk<void, void, { state: RootState; dispatch: AppDispatch }>(
  'upload/adopt-current-track',
  async (_, { dispatch, getState }) => {
    const state = getState()
    const username = selectCurrentUsername(state)
    const currTrackInfo = selectCurrentTrackInfo(state)
    const { slug, owner: currentTrackOwner } = currTrackInfo
    const ownedTrackInfo = selectTrackInfo(username, slug)(state) || defaultTrackInfo
    const { owner: ownedTrackOwner } = ownedTrackInfo
    const localTrackInState = selectIsInLocalTracks(slug)(state)
    if (localTrackInState || !ownedTrackOwner || currentTrackOwner === ownedTrackOwner) {
      console.log(`can't adopt ${currentTrackOwner}'s track ${slug}`)
      return
    }
    dispatch(
      blasterPeersSlice.actions.updateTrack({
        username,
        trackInfo: {
          ...ownedTrackInfo,
          owner: currentTrackOwner,
        },
      })
    )
  }
)

export { adoptCurrentTrack, uploadAllDirty }
